import Icon from '@mui/icons-material/CheckCircle'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { BodyPaper, Header } from '../../components/Layout'

/**
 * `removeURLScheme` is a utility function that removes the URL scheme (http or https)
 * from a given URL string. This is useful for displaying URLs without the scheme
 * in the UI.
 * @param helpURI - The URL string from which to remove the scheme.
 * @returns {string} The URL string without the scheme.
 */
function removeURLScheme(helpURI: string): string {
  return helpURI.replace(/https?:\/\//, '')
}

/**
 * `ApprovalConfirmationPage` is a React functional component displaying a confirmation
 * message to guardians that have granted their consent to a player. It shows a link to
 * the Bethesda help page.
 * @constructor
 */
const ApprovalConfirmationPage: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation('vpc')

  // e.g., https://help.bethesda.net, https://aide.bethesda.net
  const helpURI = t('common:data.helpUri')

  return (
    <>
      <Header title={t('completeRequestPage.title')} subtitle={''} />
      <BodyPaper
        data-testid="approval-confirmation-page"
        sx={{
          pt: 17.75,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Icon
            htmlColor={theme.palette.success.main}
            sx={{
              mb: 5,
              width: '4rem',
              height: '4rem',
            }}
          />
          <Typography variant="h4" component="h2" sx={{ mb: 1.5, textAlign: 'center' }}>
            {t('approvalConfirmationPage.title')}
          </Typography>
          <Typography sx={{ mb: 2.5, textAlign: 'center' }}>
            {t('approvalConfirmationPage.subtitle')}&nbsp;
            <Link
              to={helpURI}
              style={{
                color: theme.palette.primary.dark,
                fontWeight: 'bold',
                textDecoration: 'underline',
              }}
            >
              {/* e.g., help.bethesda.net, aide.bethesda.net */}
              {removeURLScheme(helpURI)}
            </Link>
          </Typography>
        </Box>
      </BodyPaper>
    </>
  )
}

export default ApprovalConfirmationPage
